import { useCallback, useEffect, useRef } from 'react';
import { TURNSTILE_SITE_KEY } from '../config/turnstile';

/**
 * Cloudflare Turnstile en mode « managed » : invisible pour un humain normal, un
 * défi n'apparaît (dans le conteneur) que si Cloudflare a un doute. Le script n'est
 * chargé qu'à la demande (preload au focus du champ) pour ne rien coûter aux
 * visiteurs qui n'utilisent pas le formulaire. Un token est à usage unique : on
 * rend un widget neuf à chaque soumission.
 */

interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  size?: 'normal' | 'compact' | 'flexible';
  theme?: 'light' | 'dark' | 'auto';
  callback?: (token: string) => void;
  'error-callback'?: (code?: string) => void;
  'timeout-callback'?: () => void;
  'expired-callback'?: () => void;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
// Large : si Cloudflare affiche un défi interactif, l'humain doit avoir le temps de cliquer
const TOKEN_TIMEOUT_MS = 60_000;

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('turnstile_script_failed'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export function useTurnstile(action: string, theme: 'light' | 'dark' = 'light') {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const removeWidget = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch {
        // widget déjà retiré
      }
    }
    widgetIdRef.current = null;
  }, []);

  useEffect(() => removeWidget, [removeWidget]);

  /** À appeler au focus du champ : charge le script en avance pour une soumission instantanée. */
  const preload = useCallback(() => {
    if (TURNSTILE_SITE_KEY) void loadTurnstileScript().catch(() => undefined);
  }, []);

  /** Obtient un token frais. Rejette avec `turnstile_*` si Cloudflare est injoignable ou refuse. */
  const getToken = useCallback(async (): Promise<string> => {
    if (!TURNSTILE_SITE_KEY) return '';
    await loadTurnstileScript();
    const turnstile = window.turnstile;
    const container = containerRef.current;
    if (!turnstile || !container) throw new Error('turnstile_unavailable');

    removeWidget();
    return new Promise<string>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        removeWidget();
        reject(new Error('turnstile_timeout'));
      }, TOKEN_TIMEOUT_MS);
      const done = (fn: () => void) => {
        window.clearTimeout(timer);
        fn();
      };
      try {
        widgetIdRef.current = turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          theme,
          size: 'flexible',
          appearance: 'interaction-only',
          callback: (token) => done(() => resolve(token)),
          'error-callback': (code) => done(() => reject(new Error(`turnstile_error_${code ?? 'unknown'}`))),
          'timeout-callback': () => done(() => reject(new Error('turnstile_timeout'))),
          'expired-callback': () => done(() => reject(new Error('turnstile_expired'))),
        });
      } catch (err) {
        done(() => reject(err instanceof Error ? err : new Error('turnstile_render_failed')));
      }
    });
  }, [action, theme, removeWidget]);

  return { containerRef, preload, getToken, reset: removeWidget };
}
